import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useSetAtom } from 'jotai';
import { BuildingOfficeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

import { Title } from '@restworld/ui-ds';

import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';
import { currentCompanyActionAtom, isCompanyForeignAtom, selectedCompanyAtom } from '../../employer-companies.atoms';

/** Conventional FIC values for foreign companies */
const FOREIGN_COMPANY_DEFAULTS = {
  province_code: 'EE',
  zip_code: '00000',
  sdi_code: 'XXXXXXX',
  fiscal_code: '',
};

export type CompanyTypeChoiceRef = {
  submit: () => void;
};

interface CompanyTypeChoiceProps {
  hideCta?: boolean;
  onChooseItalian?: () => void;
  onChooseForeign?: () => void;
}

const CompanyTypeChoice = forwardRef<CompanyTypeChoiceRef, CompanyTypeChoiceProps>(
  ({ hideCta, onChooseItalian, onChooseForeign }, ref) => {
    const { t } = useTranslation('payment-and-invoices');
    const footerContext = useContext(EmployerCompanyModalFooterContext);
    const setCurrentCompanyAction = useSetAtom(currentCompanyActionAtom);
    const setIsCompanyForeign = useSetAtom(isCompanyForeignAtom);
    const setSelectedCompany = useSetAtom(selectedCompanyAtom);

    const handleChooseItalian = useCallback(() => {
      setIsCompanyForeign(false);
      if (onChooseItalian) {
        onChooseItalian();
      } else {
        setCurrentCompanyAction('searchCompany');
      }
    }, [onChooseItalian, setCurrentCompanyAction, setIsCompanyForeign]);

    const handleChooseForeign = useCallback(() => {
      setIsCompanyForeign(true);
      setSelectedCompany(FOREIGN_COMPANY_DEFAULTS);
      if (onChooseForeign) {
        onChooseForeign();
      } else {
        setCurrentCompanyAction('createCompany');
      }
    }, [onChooseForeign, setCurrentCompanyAction, setIsCompanyForeign, setSelectedCompany]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        // No-op: choice is made by clicking a card directly
      },
    }));

    // Hide footer in modal context (no next/cancel needed, cards navigate directly)
    useEffect(() => {
      if (footerContext) {
        footerContext.setFooter(null);
        return () => footerContext.setFooter(null);
      }
    }, [footerContext]);

    return (
      <Wrapper>
        <Title textColor="neutral-700" fontWeight="semibold" twStyle={tw`text-lg text-center w-full mb-2`}>
          {t('choose_company_type')}
        </Title>
        <Subtitle>{t('choose_company_type_subtitle')}</Subtitle>
        <CardsWrapper>
          <ChoiceCard onClick={handleChooseItalian}>
            <IconWrapper>
              <BuildingOfficeIcon tw="h-8 w-8 text-primary-500" />
            </IconWrapper>
            <CardTitle>{t('italian_company')}</CardTitle>
            <CardDescription>{t('italian_company_description')}</CardDescription>
          </ChoiceCard>
          <ChoiceCard onClick={handleChooseForeign}>
            <IconWrapper>
              <GlobeAltIcon tw="h-8 w-8 text-primary-500" />
            </IconWrapper>
            <CardTitle>{t('foreign_company')}</CardTitle>
            <CardDescription>{t('foreign_company_description')}</CardDescription>
          </ChoiceCard>
        </CardsWrapper>
      </Wrapper>
    );
  }
);

CompanyTypeChoice.displayName = 'CompanyTypeChoice';
export default CompanyTypeChoice;

const Wrapper = styled.div`
  ${tw`flex flex-col items-center justify-center h-full px-4 md:px-0`}
`;
const CardsWrapper = styled.div`
  ${tw`grid grid-cols-1 gap-6 w-full max-w-[500px] md:grid-cols-2`}
`;
const ChoiceCard = styled.button`
  ${tw`flex flex-col items-center gap-3 p-8 rounded-2xl border-2 border-neutral-40 bg-neutral-10 cursor-pointer transition-all hover:(border-primary-500 shadow-md) focus:(outline-none border-primary-500 shadow-md)`}
`;
const IconWrapper = styled.div`
  ${tw`flex items-center justify-center w-14 h-14 rounded-full bg-primary-50`}
`;
const CardTitle = styled.span`
  ${tw`text-base font-semibold text-neutral-700`}
`;
const CardDescription = styled.span`
  ${tw`text-sm text-neutral-500 text-center`}
`;
const Subtitle = styled.p`
  ${tw`text-base font-normal text-neutral-500 text-center w-full mt-0 mb-10 lg:mb-16`}
`;
